import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Loader2,
  Clock,
  Users,
  DollarSign,
  Globe,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  Shield,
  List,
  Package,
  Info,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';

interface Service {
  id: string;
  name: string;
  slug: string;
  url: string;
  description: string | null;
  lang: string | null;
  market: string | null;
  status: string | null;
  duration_minutes: number | null;
  group_size_min: number | null;
  group_size_max: number | null;
  price_model: string | null;
  price_from_cents: number | null;
  currency: string | null;
  location: string | null;
  inclusions: string[] | null;
  exclusions: string[] | null;
  primary_sector_id: number | null;
  attributes: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

/** Fields tracked for enrichment completeness */
const ENRICHMENT_FIELDS: Array<{ key: keyof Service; label: string }> = [
  { key: 'duration_minutes', label: 'Duration' },
  { key: 'group_size_min', label: 'Group size (min)' },
  { key: 'group_size_max', label: 'Group size (max)' },
  { key: 'price_model', label: 'Price model' },
  { key: 'price_from_cents', label: 'Price' },
  { key: 'inclusions', label: 'Inclusions' },
  { key: 'exclusions', label: 'Exclusions' },
  { key: 'location', label: 'Location' },
  { key: 'lang', label: 'Language' },
  { key: 'description', label: 'Description' },
];

function computeCompleteness(service: Service): number {
  let filled = 0;
  for (const { key } of ENRICHMENT_FIELDS) {
    const val = service[key];
    if (val !== null && val !== undefined) {
      if (Array.isArray(val) && val.length === 0) continue;
      if (typeof val === 'string' && val.trim() === '') continue;
      filled++;
    }
  }
  return Math.round((filled / ENRICHMENT_FIELDS.length) * 100);
}

function completenessColor(pct: number): string {
  if (pct >= 80) return 'text-green-600';
  if (pct >= 50) return 'text-yellow-600';
  return 'text-red-600';
}

function formatCents(cents: number | null, currency: string | null): string {
  if (cents === null) return '-';
  const amount = cents / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(amount);
}

function ServiceDetail({ service, onBack }: { service: Service; onBack: () => void }) {
  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-2">
        <ChevronLeft className="h-4 w-4 mr-1" /> Back to list
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">{service.name}</CardTitle>
              <CardDescription className="mt-1">
                {service.slug}
                {service.url && (
                  <a
                    href={service.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 inline-flex items-center text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" /> View page
                  </a>
                )}
              </CardDescription>
            </div>
            <Badge variant={service.status === 'active' ? 'default' : 'secondary'}>
              {service.status || 'unknown'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Completeness */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">Enrichment completeness</span>
              <span className={`text-sm font-bold ${completenessColor(computeCompleteness(service))}`}>
                {computeCompleteness(service)}%
              </span>
            </div>
            <Progress value={computeCompleteness(service)} className="h-2" />
          </div>

          {/* Typed fields grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldRow
              icon={<Clock className="h-4 w-4" />}
              label="Duration"
              value={service.duration_minutes != null ? `${service.duration_minutes} min` : null}
            />
            <FieldRow
              icon={<Users className="h-4 w-4" />}
              label="Group size"
              value={
                service.group_size_min != null || service.group_size_max != null
                  ? `${service.group_size_min ?? '?'} - ${service.group_size_max ?? '?'}`
                  : null
              }
            />
            <FieldRow
              icon={<DollarSign className="h-4 w-4" />}
              label="Price"
              value={
                service.price_from_cents != null
                  ? `${formatCents(service.price_from_cents, service.currency)} (${service.price_model || 'flat'})`
                  : null
              }
            />
            <FieldRow
              icon={<Globe className="h-4 w-4" />}
              label="Language"
              value={service.lang}
            />
            <FieldRow
              icon={<Package className="h-4 w-4" />}
              label="Location"
              value={service.location}
            />
            <FieldRow
              icon={<Shield className="h-4 w-4" />}
              label="Price model"
              value={service.price_model}
            />
          </div>

          {/* Lists */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ListField
              icon={<CheckCircle2 className="h-4 w-4 text-green-500" />}
              label="Inclusions"
              items={service.inclusions}
            />
            <ListField
              icon={<XCircle className="h-4 w-4 text-red-500" />}
              label="Exclusions"
              items={service.exclusions}
            />
          </div>

          {/* Description */}
          {service.description && (
            <div>
              <h4 className="text-sm font-medium mb-1">Description</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{service.description}</p>
            </div>
          )}

          {/* Missing fields */}
          {computeCompleteness(service) < 100 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Missing fields:{' '}
                {ENRICHMENT_FIELDS.filter(({ key }) => {
                  const val = service[key];
                  if (val === null || val === undefined) return true;
                  if (Array.isArray(val) && val.length === 0) return true;
                  if (typeof val === 'string' && val.trim() === '') return true;
                  return false;
                })
                  .map(({ label }) => label)
                  .join(', ')}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FieldRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
}) {
  const filled = value != null && value.trim() !== '';
  return (
    <div className="flex items-center gap-3 p-2 rounded-md bg-muted/30">
      <span className={filled ? 'text-foreground' : 'text-muted-foreground/50'}>{icon}</span>
      <div className="flex-1 min-w-0">
        <span className="text-xs text-muted-foreground">{label}</span>
        <p className={`text-sm truncate ${filled ? 'font-medium' : 'text-muted-foreground italic'}`}>
          {filled ? value : 'Not set'}
        </p>
      </div>
      {filled ? (
        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-muted-foreground/40 shrink-0" />
      )}
    </div>
  );
}

function ListField({
  icon,
  label,
  items,
}: {
  icon: React.ReactNode;
  label: string;
  items: string[] | null;
}) {
  const hasItems = items && items.length > 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-medium">{label}</span>
        {hasItems ? (
          <Badge variant="secondary" className="ml-auto">{items.length}</Badge>
        ) : (
          <Badge variant="outline" className="ml-auto text-muted-foreground">None</Badge>
        )}
      </div>
      {hasItems && (
        <ul className="pl-6 space-y-0.5">
          {items.map((item, i) => (
            <li key={i} className="text-sm text-muted-foreground list-disc">{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ServiceEnrichmentReview() {
  const { selectedSite } = useSite();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [reEnrichingId, setReEnrichingId] = useState<string | null>(null);
  const pageSize = 25;

  const fetchServices = useCallback(async () => {
    if (!selectedSite?.id) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (search) params.set('search', search);

      const response = await authFetch(`/api/services/${selectedSite.id}?${params}`);
      if (!response.ok) throw new Error(`Failed to fetch services: ${response.status}`);
      const data = await response.json();
      setServices(data.services || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [selectedSite?.id, page, search]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleReEnrich = async (serviceId: string) => {
    if (!selectedSite?.id) return;
    setReEnrichingId(serviceId);
    try {
      const response = await authFetch(`/api/services/${selectedSite.id}/${serviceId}/re-enrich`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Re-enrichment request failed');
      await fetchServices();
    } catch (err) {
      console.error('Re-enrichment failed:', err);
    } finally {
      setReEnrichingId(null);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  // Aggregate stats
  const avgCompleteness = services.length > 0
    ? Math.round(services.reduce((sum, s) => sum + computeCompleteness(s), 0) / services.length)
    : 0;
  const fullyEnriched = services.filter(s => computeCompleteness(s) === 100).length;

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>Select a site from the header to review service enrichment.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (selectedService) {
    return (
      <div className="p-6">
        <ServiceDetail service={selectedService} onBack={() => setSelectedService(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Service Enrichment Review</h2>
        <p className="text-muted-foreground">
          Review enrichment completeness for services and trigger re-enrichment where needed.
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total services</p>
                <p className="text-2xl font-bold">{total}</p>
              </div>
              <List className="h-8 w-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. completeness</p>
                <p className={`text-2xl font-bold ${completenessColor(avgCompleteness)}`}>
                  {avgCompleteness}%
                </p>
              </div>
              <Progress value={avgCompleteness} className="w-16 h-2" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Fully enriched</p>
                <p className="text-2xl font-bold">
                  {fullyEnriched} <span className="text-sm text-muted-foreground font-normal">/ {services.length}</span>
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search services..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
          />
        </div>
        <Button variant="outline" size="icon" onClick={fetchServices} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Service list */}
      {loading && services.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : services.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No services found for this site.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {services.map((service) => {
            const pct = computeCompleteness(service);
            return (
              <Card
                key={service.id}
                className="cursor-pointer hover:bg-muted/40 transition-colors"
                onClick={() => setSelectedService(service)}
              >
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-4">
                    {/* Completeness indicator */}
                    <div className="w-12 text-center shrink-0">
                      <span className={`text-sm font-bold ${completenessColor(pct)}`}>{pct}%</span>
                    </div>

                    {/* Name + meta */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{service.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        {service.duration_minutes && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {service.duration_minutes}min
                          </span>
                        )}
                        {service.price_from_cents != null && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" /> {formatCents(service.price_from_cents, service.currency)}
                          </span>
                        )}
                        {service.lang && (
                          <span className="flex items-center gap-1">
                            <Globe className="h-3 w-3" /> {service.lang}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Status badge */}
                    <Badge variant={pct === 100 ? 'default' : pct >= 50 ? 'secondary' : 'destructive'}>
                      {pct === 100 ? 'Complete' : pct >= 50 ? 'Partial' : 'Incomplete'}
                    </Badge>

                    {/* Re-enrich button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={reEnrichingId === service.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReEnrich(service.id);
                      }}
                    >
                      {reEnrichingId === service.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
